import { apiUrl } from "../../utils/api";
import type { BigAnswers } from "./types";
import { buildDescription, damageTypeLabel } from "./serialize";

export interface SubmitResult {
  ok: true;
  referenceCode: string;
  id: string;
}

// existing backend enum: kfz | immobilie | sonstiges
function backendDamageType(t: BigAnswers["damageType"]): string {
  if (t === "gebaeude") return "immobilie";
  if (t === "kfz") return "kfz";
  return "sonstiges";
}

function backendKfzSubject(a: BigAnswers): string | undefined {
  if (a.damageType !== "kfz") return undefined;
  if (a.kfzSchaeden.includes("eigenes")) return "eigenes_fahrzeug";
  if (a.kfzSchaeden.includes("fremdes")) return "fremdes_fahrzeug";
  if (a.kfzSchaeden.includes("person")) return "person";
  if (a.kfzSchaeden.includes("sonstige")) return "sonstige_schaeden";
  return undefined;
}

function shortLocation(a: BigAnswers): string | undefined {
  let s = "";
  if (a.damageType === "gebaeude")
    s = [a.gebStrasse, `${a.gebPlz} ${a.gebOrt}`.trim(), a.gebObjektNr]
      .filter(Boolean)
      .join(", ");
  else if (a.damageType === "kfz") s = a.kfzOrt;
  else s = [a.sonStrasse, `${a.sonPlz} ${a.sonOrt}`.trim()].filter(Boolean).join(", ");
  s = s.trim();
  return s ? s.slice(0, 500) : undefined;
}

function coreZones(a: BigAnswers): string[] {
  if (a.damageType === "gebaeude") return a.gebEinheiten.map((e) => e.etage);
  if (a.damageType === "kfz")
    return Array.from(
      new Set([...a.kfzEigenZonen, ...a.kfzFremdFahrzeuge.flatMap((f) => f.zonen)]),
    );
  return [];
}

function coreCause(a: BigAnswers): string | undefined {
  let c: string | null = null;
  if (a.damageType === "gebaeude") c = a.gebSchadenart;
  else if (a.damageType === "kfz") c = a.kfzArt;
  else c = a.sonBetroffen;
  return c ? c.slice(0, 64) : undefined;
}

function personInjured(a: BigAnswers): string | undefined {
  const v =
    a.damageType === "kfz"
      ? a.kfzPersonen.map((p) => p.name).filter(Boolean).join(", ")
      : a.damageType === "sonstiges" && a.sonBetroffen === "mensch"
        ? `${a.sonMenschContact.vorname} ${a.sonMenschContact.name}`.trim()
        : "";
  return v.trim() ? v.trim().slice(0, 500) : undefined;
}

export async function submitBigSchadenmeldung(
  a: BigAnswers,
): Promise<SubmitResult> {
  const contactName = `${a.melderVorname} ${a.melderName}`.trim() || damageTypeLabel(a.damageType);
  const payload = {
    damageType: backendDamageType(a.damageType),
    kfzSubject: backendKfzSubject(a),
    personInjured: personInjured(a),
    occurredAt: a.schadenDatum ? new Date(a.schadenDatum).toISOString() : undefined,
    locationZones: coreZones(a),
    locationDetail: shortLocation(a),
    cause: coreCause(a),
    description: buildDescription(a),
    contactName: contactName.slice(0, 255),
    contactEmail: a.melderEmail.trim(),
    contactPhone: a.melderTel.trim() || a.melderHandy.trim() || undefined,
    contractNumber: a.melderVorgangsnummer.trim() || undefined,
    consentGiven: a.consentGiven,
  };

  const res = await fetch(apiUrl("public/schadenmeldung"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    let detail = `${res.status}`;
    try {
      const body = await res.json();
      if (body?.message) detail = String(body.message);
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  return (await res.json()) as SubmitResult;
}

export interface UploadResult {
  ok: true;
  id: number;
  name: string;
}

// Upload a single photo/document for an already-submitted Schadenmeldung.
export async function uploadSchadenmeldungFile(
  referenceCode: string,
  file: File,
): Promise<UploadResult> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(
    apiUrl(`public/schadenmeldung/${encodeURIComponent(referenceCode)}/upload`),
    { method: "POST", body: form },
  );
  if (!res.ok) {
    let detail = `${res.status}`;
    try {
      const body = await res.json();
      if (body?.message) detail = String(body.message);
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  return (await res.json()) as UploadResult;
}
